import { useState, useEffect } from 'react'

interface PriceData {
  currentPrice: string
  comparePrice: string
  isLoading: boolean
}

const PRICE_MAP: Record<string, { current: string; compare: string }> = {
  US: { current: '$14.90', compare: '$34.90' },
  EC: { current: '$14.90', compare: '$34.90' },
  MX: { current: '$327.12 MXN', compare: '$750.00 MXN' },
  CA: { current: '$23.10 CAD', compare: '$49.00 CAD' },
  CL: { current: '$17.216 CLP', compare: '$38.500 CLP' },
  CO: { current: '$57.667 COP', compare: '$125.000 COP' },
  PE: { current: 'S/ 55.00', compare: 'S/ 120.00' },
  UY: { current: '$637.00 UYU', compare: '$1.450 UYU' },
  PY: { current: 'Gs. 101.459', compare: 'Gs. 230.000' },
  BO: { current: 'Bs 109.00', compare: 'Bs 240.00' },
  AR: { current: 'ARS\xA0$14.990', compare: 'ARS\xA0$30.990' },
}

const DEFAULT_PRICES = PRICE_MAP.US

let cachedCountry: string | null = null

async function fetchWithTimeout(url: string, ms: number): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)
  try {
    const res = await fetch(url, { signal: controller.signal })
    return res
  } finally {
    clearTimeout(timer)
  }
}

async function detectCountryCode(): Promise<string> {
  // Try multiple geo APIs as fallbacks
  const apis = [
    {
      url: 'https://ipwho.is/',
      extract: (data: any) => data.country_code,
    },
    {
      url: 'https://ip-api.com/json/?fields=countryCode',
      extract: (data: any) => data.countryCode,
    },
    {
      url: 'https://ipapi.co/json/',
      extract: (data: any) => data.country_code,
    },
  ]

  for (const api of apis) {
    try {
      const response = await fetchWithTimeout(api.url, 4000)
      if (!response.ok) continue
      const data = await response.json()
      const code = api.extract(data)?.toUpperCase()
      if (code && code.length === 2) return code
    } catch {
      continue
    }
  }

  return ''
}

export function useGeoPricing(): PriceData {
  const [prices, setPrices] = useState<{ current: string; compare: string }>(
    DEFAULT_PRICES,
  )
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (cachedCountry) {
      setPrices(PRICE_MAP[cachedCountry] || DEFAULT_PRICES)
      setIsLoading(false)
      return
    }

    detectCountryCode().then((countryCode) => {
      cachedCountry = countryCode
      setPrices(PRICE_MAP[countryCode] || DEFAULT_PRICES)
      setIsLoading(false)
    })
  }, [])

  return {
    currentPrice: prices.current,
    comparePrice: prices.compare,
    isLoading,
  }
}
